import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { useSettings } from "../../contexts/SettingsContext";
import debugx from "debugx";

interface DebugConsoleProps {
  isVisible: boolean;
  isFullScreen: boolean;
}

type DebugLogEntry = {
  namespace: string;
  level: string;
  message: string;
  formattedMessage?: string;
  args: any[];
  timestamp: number;
  template?: string; // The template string used for the log (e.g., "Setting rotation to %s°")
};

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  entry: DebugLogEntry | null;
  element: HTMLElement | null;
}

export function DebugConsole({
  isVisible,
}: Omit<DebugConsoleProps, "isFullScreen">) {
  const [activityBarTop, setActivityBarTop] = useState<number>(0);
  const [isAtBottom, setIsAtBottom] = useState<boolean>(true);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [textSize, setTextSize] = useState<"small" | "normal">("normal");
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    entry: null,
    element: null,
  });
  const [suppressedPatterns, setSuppressedPatterns] = useState<Set<string>>(
    new Set(),
  );
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const logCountRef = useRef<number>(0);
  const isAtBottomRef = useRef<boolean>(true);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const MAX_LOGS = 2000;
  const LONG_PRESS_DURATION = 500; // ms
  const DRAG_THRESHOLD = 10; // pixels
  const { openSettings } = useSettings();

  // Load suppressed patterns from localStorage
  useEffect(() => {
    const loadPatterns = () => {
      const stored = localStorage.getItem("debugSuppressedPatterns");
      if (stored) {
        try {
          const patterns = JSON.parse(stored);
          setSuppressedPatterns(new Set(patterns));
        } catch (e) {
          console.error("Failed to load suppressed patterns:", e);
        }
      } else {
        setSuppressedPatterns(new Set());
      }
    };

    loadPatterns();

    // Listen for changes from settings tab
    const handlePatternsChanged = () => {
      loadPatterns();
    };

    window.addEventListener(
      "debugSuppressedPatternsChanged" as any,
      handlePatternsChanged,
    );

    return () => {
      window.removeEventListener(
        "debugSuppressedPatternsChanged" as any,
        handlePatternsChanged,
      );
    };
  }, []);

  // Save suppressed patterns to localStorage
  const saveSuppressedPatterns = (patterns: Set<string>) => {
    localStorage.setItem(
      "debugSuppressedPatterns",
      JSON.stringify(Array.from(patterns)),
    );
    setSuppressedPatterns(patterns);

    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new CustomEvent("debugSuppressedPatternsChanged"));
  };

  // Check if a log entry should be suppressed
  const isLogSuppressed = (entry: DebugLogEntry): boolean => {
    for (const pattern of suppressedPatterns) {
      // Check exact namespace suppression
      if (pattern === entry.namespace) return true;

      // Check namespace:template suppression (exact match of namespace + template)
      if (
        entry.template &&
        pattern === `${entry.namespace}:${entry.template}`
      ) {
        return true;
      }
    }
    return false;
  };

  // Set up debug hooks to capture logs
  useEffect(() => {
    const cleanup = (debugx as any).debugHooks.addFiltered(
      (entry: DebugLogEntry) => {
        // Skip suppressed logs
        if (isLogSuppressed(entry)) return;
        // Append new log directly to DOM instead of re-rendering
        if (logsContainerRef.current) {
          // Hide placeholder on first log
          if (logCountRef.current === 0) {
            const placeholders =
              logsContainerRef.current.querySelectorAll("div");
            placeholders.forEach((placeholder) => {
              if (
                placeholder.textContent?.includes(
                  "Debug output will appear here",
                )
              ) {
                placeholder.style.display = "none";
              }
            });
          }

          const logDiv = document.createElement("div");
          logDiv.style.cssText = `
          margin-bottom: 4px;
          word-break: break-word;
          opacity: ${entry.level === "error" ? "1" : "0.9"};
          cursor: context-menu;
          user-select: text;
        `;
          logDiv.innerHTML =
            entry.formattedMessage || `<span>${entry.message}</span>`;

          // Store entry data on the element for context menu
          (logDiv as any).__debugEntry = entry;

          // Insert before the end ref div
          const endRef = logsContainerRef.current.lastElementChild;
          if (endRef) {
            logsContainerRef.current.insertBefore(logDiv, endRef);
          } else {
            logsContainerRef.current.appendChild(logDiv);
          }
          logCountRef.current++;

          // Remove old logs if we exceed MAX_LOGS
          if (logCountRef.current > MAX_LOGS) {
            const container = logsContainerRef.current;
            const wasAtBottom = isAtBottomRef.current;
            const oldScrollTop = container.scrollTop;

            const children = Array.from(container.children);
            const firstLog = children.find(
              (child) =>
                !child.textContent?.includes("Debug output will appear here") &&
                child !== endRef,
            );
            if (firstLog) {
              const logHeight = firstLog.scrollHeight;
              container.removeChild(firstLog);
              logCountRef.current--;

              // Preserve scroll position and bottom state when removing from top
              if (!wasAtBottom) {
                container.scrollTop = Math.max(0, oldScrollTop - logHeight);
              }
            }
          }

          // Auto-scroll ONLY if isAtBottomRef is true
          if (isAtBottomRef.current && logsContainerRef.current) {
            // console.log("AUTO SCROLLING - isAtBottom:", isAtBottomRef.current);
            logsContainerRef.current.scrollTo({
              top: logsContainerRef.current.scrollHeight,
              behavior: "smooth",
            });
          } else {
            // console.log(
            //   "NOT AUTO SCROLLING - isAtBottom:",
            //   isAtBottomRef.current,
            // );
          }
        }
      },
    );

    return cleanup;
  }, [suppressedPatterns]);

  // Check if scrolled to bottom (only call during user interactions)
  const checkScrollPosition = () => {
    if (logsContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        logsContainerRef.current;

      // If no scrollable content, consider it "at bottom"
      const isBottom =
        scrollHeight <= clientHeight ||
        Math.abs(scrollHeight - clientHeight - scrollTop) < 20;
      setIsAtBottom(isBottom);
      isAtBottomRef.current = isBottom;
    }
  };

  // Scroll functions
  const scrollToTop = () => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = 0;
      setIsAtBottom(false);
      isAtBottomRef.current = false;
      console.log("SCROLL TO TOP - set isAtBottom to false");
    }
  };

  const scrollToBottom = () => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTo({
        top: logsContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
      setIsAtBottom(true);
      isAtBottomRef.current = true;
    }
  };

  // Clear logs function
  const clearLogs = () => {
    if (logsContainerRef.current) {
      logsContainerRef.current.innerHTML = `
        <div style="margin-bottom: var(--spacing-sm); opacity: 0.5; font-size: 9px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px;">
          Debug Console
        </div>
        <div style="opacity: 0.5; font-style: italic;">
          Debug output will appear here...
        </div>
        <div></div>
      `;
      logCountRef.current = 0;
      // Re-attach the end ref
      const endDiv = logsContainerRef.current.lastElementChild;
      if (endDiv && logsEndRef.current !== endDiv) {
        logsEndRef.current = endDiv as HTMLDivElement;
      }
      setIsAtBottom(true);
      isAtBottomRef.current = true;
    }
  };

  // Handle context menu (right-click and long-press)
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent | TouchEvent, isTouch = false) => {
      const target = e.target as HTMLElement;
      const logDiv = target.closest(
        'div[style*="margin-bottom"]',
      ) as HTMLElement;

      if (logDiv && (logDiv as any).__debugEntry) {
        e.preventDefault();
        e.stopPropagation();

        const entry = (logDiv as any).__debugEntry as DebugLogEntry;
        const containerRect = logsContainerRef.current?.getBoundingClientRect();

        if (containerRect) {
          let x, y;

          if (isTouch) {
            // For touch events, use the stored touch position
            if (touchStartPosRef.current) {
              x = touchStartPosRef.current.x;
              y = touchStartPosRef.current.y;
            } else {
              return; // No touch position available
            }
          } else {
            // For mouse events
            x = (e as MouseEvent).clientX;
            y = (e as MouseEvent).clientY;
          }

          // Use absolute viewport coordinates for fixed positioning
          setContextMenu({
            visible: true,
            x: x,
            y: y,
            entry,
            element: logDiv,
          });
        }
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const logDiv = target.closest(
        'div[style*="margin-bottom"]',
      ) as HTMLElement;

      if (logDiv && (logDiv as any).__debugEntry) {
        // Store initial touch position
        touchStartPosRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };

        longPressTimerRef.current = setTimeout(() => {
          // Only show context menu if finger hasn't moved much
          if (touchStartPosRef.current) {
            handleContextMenu(e, true);
            // Vibrate if available
            if ("vibrate" in navigator) {
              navigator.vibrate(50);
            }
          }
        }, LONG_PRESS_DURATION);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Cancel long press if finger moves too much
      if (touchStartPosRef.current && longPressTimerRef.current) {
        const deltaX = Math.abs(
          e.touches[0].clientX - touchStartPosRef.current.x,
        );
        const deltaY = Math.abs(
          e.touches[0].clientY - touchStartPosRef.current.y,
        );

        if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
          // Cancel the long press
          clearTimeout(longPressTimerRef.current);
          longPressTimerRef.current = null;
          touchStartPosRef.current = null;
        }
      }
    };

    const handleTouchEnd = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      touchStartPosRef.current = null;
    };

    const handleClick = (e: MouseEvent) => {
      // Close context menu on click outside
      if (contextMenu.visible) {
        const target = e.target as HTMLElement;
        if (!target.closest(".context-menu")) {
          setContextMenu({ ...contextMenu, visible: false });
        }
      }
    };

    const container = logsContainerRef.current;
    if (container) {
      container.addEventListener(
        "contextmenu",
        handleContextMenu as EventListener,
      );
      container.addEventListener("touchstart", handleTouchStart);
      container.addEventListener("touchmove", handleTouchMove);
      container.addEventListener("touchend", handleTouchEnd);
      container.addEventListener("touchcancel", handleTouchEnd);
      document.addEventListener("click", handleClick);

      return () => {
        container.removeEventListener(
          "contextmenu",
          handleContextMenu as EventListener,
        );
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
        container.removeEventListener("touchend", handleTouchEnd);
        container.removeEventListener("touchcancel", handleTouchEnd);
        document.removeEventListener("click", handleClick);
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
        }
      };
    }
    return undefined;
  }, [contextMenu]);

  // Handle object expansion clicks
  useEffect(() => {
    const handleObjectClick = (e: MouseEvent) => {
      // Don't expand if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }

      const target = e.target as HTMLElement;
      if (target.classList.contains("debug-object")) {
        e.stopPropagation(); // Prevent stack trace toggle
        e.preventDefault(); // Prevent default behavior
        const jsonData = target.getAttribute("data-json");
        if (jsonData) {
          // Check if already expanded
          const existingPre = target.nextElementSibling;
          if (existingPre && existingPre.tagName === "PRE") {
            // Collapse
            existingPre.remove();
            target.style.textDecoration = "underline";
          } else {
            // Expand
            const pre = document.createElement("pre");
            pre.style.cssText = `
              margin: 4px 0 4px 20px;
              padding: 6px;
              background: rgba(255,255,255,0.1);
              border-radius: 3px;
              font-size: 9px;
              border-left: 2px solid #4a9eff;
              max-height: 200px;
              overflow-y: auto;
              white-space: pre-wrap;
              user-select: text;
            `;
            pre.textContent = jsonData;

            // Prevent clicks on the expanded JSON from bubbling up, but allow text selection
            pre.addEventListener("click", (e) => {
              e.stopPropagation();
            });
            pre.addEventListener("mousedown", (e) => {
              e.stopPropagation();
            });

            target.insertAdjacentElement("afterend", pre);
            target.style.textDecoration = "none";
          }
        }
      }
    };

    document.addEventListener("click", handleObjectClick, true); // Use capturing phase
    return () => document.removeEventListener("click", handleObjectClick, true);
  }, []);

  useEffect(() => {
    const updateActivityBarPosition = () => {
      const activityBar = document.querySelector(
        ".activity-bar",
      ) as HTMLElement;
      if (activityBar) {
        const rect = activityBar.getBoundingClientRect();
        setActivityBarTop(rect.top);
      }
    };

    updateActivityBarPosition();
    window.addEventListener("resize", updateActivityBarPosition);

    const activityBar = document.querySelector(".activity-bar");
    let observer: MutationObserver | null = null;

    if (activityBar) {
      observer = new MutationObserver(() => {
        setTimeout(updateActivityBarPosition, 350);
      });
      observer.observe(activityBar, {
        attributes: true,
        attributeFilter: ["class"],
        childList: true,
        subtree: true,
      });
    }

    return () => {
      window.removeEventListener("resize", updateActivityBarPosition);
      if (observer) observer.disconnect();
    };
  }, []);

  // Add scroll event listener for user wheel/touch scrolling only
  useEffect(() => {
    const container = logsContainerRef.current;
    if (container) {
      const handleUserScroll = () => {
        checkScrollPosition();
      };

      container.addEventListener("wheel", handleUserScroll, { passive: true });
      container.addEventListener("touchmove", handleUserScroll, {
        passive: true,
      });

      return () => {
        container.removeEventListener("wheel", handleUserScroll);
        container.removeEventListener("touchmove", handleUserScroll);
      };
    }
    return undefined;
  }, []);

  // Add focus detection
  useEffect(() => {
    const container = logsContainerRef.current;
    if (container) {
      const handleFocusIn = () => setIsFocused(true);
      const handleFocusOut = () => setIsFocused(false);

      container.addEventListener("focusin", handleFocusIn);
      container.addEventListener("focusout", handleFocusOut);
      container.addEventListener("mouseenter", handleFocusIn);
      container.addEventListener("mouseleave", handleFocusOut);

      return () => {
        container.removeEventListener("focusin", handleFocusIn);
        container.removeEventListener("focusout", handleFocusOut);
        container.removeEventListener("mouseenter", handleFocusIn);
        container.removeEventListener("mouseleave", handleFocusOut);
      };
    }
    return undefined;
  }, []);

  if (!isVisible) return null;

  const consoleStyle: React.CSSProperties = isFullScreen
    ? {
        position: "fixed",
        top: "48px", // Below header
        left: 0,
        right: 0,
        bottom: `${window.innerHeight - activityBarTop}px`, // Same bottom as normal mode
        background: `rgba(0, 0, 0, ${isFocused ? 0.85 : 0.7})`,
        backdropFilter: "blur(5px)",
        transition: "background-color 0.2s ease-in-out",
        pointerEvents: "all",
        zIndex: 2,
        padding: "0",
        fontFamily: "var(--font-mono)",
        fontSize: "11px",
        color: "var(--text-primary)",
        display: "flex",
        flexDirection: "column",
        borderTop: "1px solid var(--border)",
      }
    : {
        position: "fixed",
        bottom: `${window.innerHeight - activityBarTop - 4}px`,
        left: 0,
        right: 0,
        height: `min(33vh, ${Math.max(0, activityBarTop - window.innerHeight * 0.55 - 8)}px)`,
        background: `rgba(0, 0, 0, ${isFocused ? 0.85 : 0.7})`,
        backdropFilter: "blur(5px)",
        transition: "background-color 0.2s ease-in-out",
        pointerEvents: "all",
        zIndex: 2,
        padding: "0",
        fontFamily: "var(--font-mono)",
        fontSize: "11px",
        color: "var(--text-primary)",
        borderTop: "1px solid var(--border)",
        display: "flex",
        flexDirection: "column",
        paddingBottom: 0,
      };

  return (
    <div className="debug-console" style={consoleStyle}>
      {/* Floating scroll controls */}
      <div
        style={{
          position: "absolute",
          top: "4px",
          right: "16px",
          display: "flex",
          gap: "4px",
          zIndex: 10,
          background: "rgba(0,0,0,0.8)",
          borderRadius: "4px",
          padding: "2px",
        }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            openSettings("debug");
          }}
          style={{
            background: "rgba(255,255,255,0.3)",
            border: "1px solid var(--border)",
            color: "var(--text-secondary)",
            padding: "2px 6px",
            borderRadius: "3px",
            fontSize: "9px",
            cursor: "pointer",
          }}
          title="Debug settings"
        >
          ⚙️
        </button>
        <button
          onClick={() => setTextSize(textSize === "small" ? "normal" : "small")}
          style={{
            background: "rgba(255,255,255,0.3)",
            border: "1px solid var(--border)",
            color: "var(--text-secondary)",
            padding: "2px 6px",
            borderRadius: "3px",
            fontSize: "9px",
            cursor: "pointer",
          }}
          title={`Text size: ${textSize}`}
        >
          {textSize === "small" ? "A+" : "A-"}
        </button>
        <button
          onClick={scrollToTop}
          style={{
            background: "rgba(255,255,255,0.3)",
            border: "1px solid var(--border)",
            color: "var(--text-secondary)",
            padding: "2px 6px",
            borderRadius: "3px",
            fontSize: "9px",
            cursor: "pointer",
          }}
          title="Scroll to top"
        >
          ↑ TOP
        </button>
        <button
          onClick={scrollToBottom}
          style={{
            background: isAtBottom
              ? "rgba(0,255,0,0.2)"
              : "rgba(255,255,255,0.1)",
            border: "1px solid var(--border)",
            color: isAtBottom ? "#00ff00" : "var(--text-secondary)",
            padding: "2px 6px",
            borderRadius: "3px",
            fontSize: "9px",
            cursor: "pointer",
          }}
          title={isAtBottom ? "At bottom" : "Click to scroll to bottom"}
        >
          {isAtBottom ? "✓ BOTTOM" : "↓ SCROLL"}
        </button>
        <button
          onClick={clearLogs}
          style={{
            background: "rgba(255,255,255,0.3)",
            border: "1px solid var(--border)",
            color: "var(--text-secondary)",
            padding: "2px 8px",
            borderRadius: "3px",
            fontSize: "10px",
            cursor: "pointer",
          }}
        >
          Clear
        </button>
        <button
          onClick={() => setIsFullScreen(!isFullScreen)}
          style={{
            background: isFullScreen
              ? "rgba(0,255,0,0.2)"
              : "rgba(255,255,255,0.3)",
            border: "1px solid var(--border)",
            color: isFullScreen ? "#00ff00" : "var(--text-secondary)",
            padding: "2px 6px",
            borderRadius: "3px",
            fontSize: "11px",
            cursor: "pointer",
            marginLeft: "8px",
          }}
          title={isFullScreen ? "Exit full screen" : "Full screen"}
        >
          {isFullScreen ? "◱" : "⛶"}
        </button>
      </div>

      {/* Scrollable logs container */}
      <div
        ref={logsContainerRef}
        className="debug-console-scroll"
        style={{
          flex: 1,
          fontFamily: "var(--font-mono)",
          fontSize: textSize === "small" ? "7px" : "10px",
          lineHeight: textSize === "small" ? "1.1" : "1.4",
          userSelect: "text",
          padding:
            "var(--spacing-md) var(--spacing-md) var(--spacing-md) var(--spacing-md)",
        }}
      >
        <div
          style={{
            marginBottom: "var(--spacing-sm)",
            opacity: 0.5,
            fontSize: "9px",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}
        >
          Debug Console {isFullScreen ? "(Full-screen)" : ""}
        </div>
        <div style={{ opacity: 0.5, fontStyle: "italic" }}>
          Debug output will appear here...
        </div>
        <div ref={logsEndRef} />
      </div>

      {/* Context Menu - Rendered via Portal */}
      {contextMenu.visible &&
        contextMenu.entry &&
        ReactDOM.createPortal(
          <div
            className="context-menu"
            ref={(el) => {
              // Adjust position after render if menu goes off screen
              if (el) {
                const menuRect = el.getBoundingClientRect();
                let adjustedX = contextMenu.x;
                let adjustedY = contextMenu.y;

                // Check if menu goes off right edge
                if (contextMenu.x + menuRect.width > window.innerWidth) {
                  adjustedX = Math.max(
                    10,
                    window.innerWidth - menuRect.width - 10,
                  );
                }

                // Check if menu goes off bottom edge
                if (contextMenu.y + menuRect.height > window.innerHeight) {
                  adjustedY = Math.max(
                    10,
                    window.innerHeight - menuRect.height - 10,
                  );
                }

                if (
                  adjustedX !== contextMenu.x ||
                  adjustedY !== contextMenu.y
                ) {
                  el.style.left = `${adjustedX}px`;
                  el.style.top = `${adjustedY}px`;
                }
              }
            }}
            style={{
              position: "fixed",
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`,
              background: "rgba(20, 20, 20, 0.98)",
              border: "1px solid var(--border)",
              borderRadius: "4px",
              padding: "4px 0",
              fontSize: "11px",
              minWidth: "200px",
              maxWidth: "300px",
              zIndex: 100000, // High enough to be above everything
              backdropFilter: "blur(10px)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
              maxHeight: "80vh",
              overflowY: "auto",
              userSelect: "none", // Disable text selection
              WebkitUserSelect: "none", // For Safari
              MozUserSelect: "none", // For Firefox
            }}
          >
            <div
              style={{
                padding: "4px 12px",
                fontSize: "9px",
                opacity: 0.7,
                borderBottom: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              {contextMenu.entry.namespace}
              {contextMenu.entry.template && (
                <div style={{ fontSize: "8px", marginTop: "2px" }}>
                  Template: {contextMenu.entry.template}
                </div>
              )}
            </div>

            <button
              onClick={() => {
                const patterns = new Set(suppressedPatterns);
                patterns.add(contextMenu.entry!.namespace);
                saveSuppressedPatterns(patterns);
                setContextMenu({ ...contextMenu, visible: false });
              }}
              style={{
                display: "block",
                width: "100%",
                padding: "6px 12px",
                background: "transparent",
                border: "none",
                color: "var(--text-primary)",
                textAlign: "left",
                cursor: "pointer",
                transition: "background 0.2s",
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              🚫 Suppress namespace: {contextMenu.entry!.namespace}
            </button>

            {contextMenu.entry.template && (
              <button
                onClick={() => {
                  const patterns = new Set(suppressedPatterns);
                  patterns.add(
                    `${contextMenu.entry!.namespace}:${contextMenu.entry!.template}`,
                  );
                  saveSuppressedPatterns(patterns);
                  setContextMenu({ ...contextMenu, visible: false });
                }}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "6px 12px",
                  background: "transparent",
                  border: "none",
                  color: "var(--text-primary)",
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                🎯 Suppress this message type
              </button>
            )}

            <div
              style={{
                borderTop: "1px solid rgba(255,255,255,0.1)",
                margin: "4px 0",
              }}
            />

            <button
              onClick={() => {
                // Copy namespace to clipboard
                navigator.clipboard.writeText(contextMenu.entry!.namespace);
                setContextMenu({ ...contextMenu, visible: false });
              }}
              style={{
                display: "block",
                width: "100%",
                padding: "6px 12px",
                background: "transparent",
                border: "none",
                color: "var(--text-primary)",
                textAlign: "left",
                cursor: "pointer",
                transition: "background 0.2s",
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              📋 Copy namespace
            </button>

            <button
              onClick={() => {
                // Copy full log message
                if (contextMenu.element) {
                  const text = contextMenu.element.textContent || "";
                  navigator.clipboard.writeText(text);
                }
                setContextMenu({ ...contextMenu, visible: false });
              }}
              style={{
                display: "block",
                width: "100%",
                padding: "6px 12px",
                background: "transparent",
                border: "none",
                color: "var(--text-primary)",
                textAlign: "left",
                cursor: "pointer",
                transition: "background 0.2s",
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              📄 Copy message
            </button>

            {suppressedPatterns.size > 0 && (
              <>
                <div
                  style={{
                    borderTop: "1px solid rgba(255,255,255,0.1)",
                    margin: "4px 0",
                  }}
                />
                <button
                  onClick={() => {
                    saveSuppressedPatterns(new Set());
                    setContextMenu({ ...contextMenu, visible: false });
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "6px 12px",
                    background: "transparent",
                    border: "none",
                    color: "#ff6b6b",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,107,107,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  🔄 Clear all suppressions ({suppressedPatterns.size})
                </button>
              </>
            )}

            <button
              onClick={() => {
                openSettings("debug");
                setContextMenu({ ...contextMenu, visible: false });
              }}
              style={{
                display: "block",
                width: "100%",
                padding: "6px 12px",
                background: "transparent",
                border: "none",
                color: "var(--text-primary)",
                textAlign: "left",
                cursor: "pointer",
                transition: "background 0.2s",
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              ⚙️ Debug settings
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
